import React from 'react';

const DownloadsPage = () => {
  const downloads = [
    {
      id: 1,
      name: 'MiBOT Onboarding Mobile App',
      version: 'v1.8',
      platform: 'Android',
      fileSize: '6.1 MB',
      fileName: 'MiBOT-Onboarding-v1.8.apk',
      icon: '📱',
      description: 'Android mobile application for MiBOT Onboarding system. Install this app on your Android device to access onboarding on the go.',
      releaseDate: 'January 2026',
      features: [
        'Face ID / Touch ID authentication',
        'Access all companies and branches',
        'Real-time onboarding progress tracking',
        'Offline-capable interface',
        'Push notifications for updates'
      ]
    }
  ];

  const handleDownload = (fileName) => {
    const link = document.createElement('a');
    link.href = `/downloads/${fileName}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-900 dark:to-purple-900">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:30px_30px]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm mb-6 shadow-lg">
              <span className="text-5xl">📥</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Downloads
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Get the latest version of MiBOT Onboarding mobile app and other resources
            </p>
          </div>
        </div>
      </div>

      {/* Downloads Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-6">
          {downloads.map((download) => (
            <div
              key={download.id}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200 dark:border-slate-700"
            >
              <div className="p-8">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Left Column - Icon & Basic Info */}
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white text-5xl shadow-lg">
                      {download.icon}
                    </div>
                  </div>

                  {/* Middle Column - Details */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                          {download.name}
                        </h2>
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm font-semibold rounded-full">
                          {download.version}
                        </span>
                      </div>
                      <p className="text-slate-600 dark:text-slate-400">
                        {download.description}
                      </p>
                    </div>

                    {/* Meta Info */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <span className="font-semibold">Platform:</span>
                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded">{download.platform}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <span className="font-semibold">Size:</span>
                        <span>{download.fileSize}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <span className="font-semibold">Released:</span>
                        <span>{download.releaseDate}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Key Features:
                      </h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {download.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <span className="text-green-500 mt-0.5">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Right Column - Download Button */}
                  <div className="flex-shrink-0 flex items-center lg:items-start">
                    <button
                      onClick={() => handleDownload(download.fileName)}
                      className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3 w-full lg:w-auto justify-center"
                    >
                      <svg 
                        className="w-6 h-6" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" 
                        />
                      </svg>
                      <div className="text-left">
                        <div className="text-sm opacity-90">Download</div>
                        <div className="text-lg font-bold">APK File</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Installation Instructions */}
              <div className="bg-slate-50 dark:bg-slate-900/50 px-8 py-6 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <span>📖</span>
                  Installation Instructions
                </h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-400">
                  <li>Download the APK file to your Android device</li>
                  <li>Open the downloaded file (you may need to enable "Install from unknown sources" in Settings)</li>
                  <li>Follow the on-screen prompts to install the app</li>
                  <li>Launch MiBOT Onboarding and log in with your credentials</li>
                  <li>Enable Face ID/Touch ID for quick access (optional)</li>
                </ol>
              </div>
            </div>
          ))}
        </div>

        {/* Info Card */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-2xl">
                ℹ️
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Need Help?
              </h3>
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                If you experience any issues installing or using the mobile app, please contact your system administrator 
                or reach out to MiBOT support for assistance.
              </p>
            </div>
          </div>
        </div>

        {/* iOS Coming Soon Card */}
        <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-2xl">
                🍎
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">
                iOS App Coming Soon
              </h3>
              <p className="text-purple-800 dark:text-purple-200 text-sm">
                We're working on bringing MiBOT Onboarding to iOS devices. The app will be available through 
                Apple Business Manager for enterprise distribution.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadsPage;
